import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { useTenantApp } from '@/hooks';
import { formatTenantAppTime } from '@/utils/date';
import { Icon } from '../../Icon';

const CleaningWidget = ({
    updatedAt,
    isOperational,
    t
}: {
    updatedAt?: number; // timestamp in milliseconds
    isOperational?: boolean;
    t: any;
}) => {
    const { app } = useTenantApp();

    // if not present, we assume the API doesn't provide this info and we hide the widget entirely
    if (updatedAt === undefined && isOperational === undefined) return null;

    const getCleaningInfo = (timestamp: number) => {
        try {
            const date = new Date(timestamp);
            return {
                distance: formatDistanceToNow(date, { addSuffix: true, locale: it }),
                exactTime: formatTenantAppTime(timestamp, app?.timezone)
            };
        } catch {
            return { distance: '', exactTime: '' };
        }
    };

    const cleaningInfo = updatedAt ? getCleaningInfo(updatedAt) : null;

    return (
        <div className="bg-base-200/20 border border-base-300/10 rounded-2xl overflow-hidden">
            <div className="p-5 flex flex-col gap-4">

                {/* Upper Row: Operational Status */}
                {isOperational !== undefined && (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`${isOperational ? 'bg-success/10 text-success' : 'bg-error/10 text-error'} rounded-2xl p-2.5 flex items-center justify-center shrink-0 border border-base-200`}>
                                {isOperational ? (
                                    <Icon name="build_circle" className="text-2xl" />
                                ) : (
                                    <Icon name="warning" className="text-2xl" />
                                )}
                            </div>
                            <div>
                                <h3 className="text-[10px] font-black uppercase tracking-widest opacity-40">
                                    {t('poiModal.status')}
                                </h3>
                                <p className="text-sm font-bold">
                                    {isOperational ? t('poiModal.isOperational') : t('poiModal.notOperational')}
                                </p>
                            </div>
                        </div>

                        {/* Immediate visual badge */}
                        <div className={`badge ${isOperational ? 'badge-success' : 'badge-error'} badge-sm font-bold p-3`}>
                            {isOperational ? t('common.active') : t('common.inactive')}
                        </div>
                    </div>
                )}

                {/* Internal separator if we have both data */}
                {isOperational !== undefined && updatedAt && (
                    <div className="h-px bg-base-300/20 w-full"></div>
                )}

                {/* Lower Row: Last Cleaning / Maintenance */}
                {updatedAt && cleaningInfo && (
                    <div className="flex items-center gap-3 tooltip tooltip-left" data-tip={`Ore ${cleaningInfo.exactTime}`}>
                        <div className="pb-1 pt-2 pl-2 pr-2  rounded-lg bg-info/10 text-info">
                            <Icon name="cleaning" className="text-xl" />
                        </div>
                        <div>
                            <h3 className="text-[10px] font-black uppercase tracking-widest opacity-40">
                                {t('poiModal.lastCleaning')}
                            </h3>
                            <p className="text-sm font-bold text-base-content/90">
                                {t('poiModal.cleaned')} {cleaningInfo.distance}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CleaningWidget;